import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import ToolbarPlugin from "./plugins/ToolbarPlugin/ToolbarPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import styles from "./AscRichTextEditor.module.scss";
import TreeViewPlugin from "./plugins/TreeViewPlugin/TreeViewPlugin";
import { ToolBarOptions } from "./config/constants";
import EditablePlugin from "./plugins/EditablePlugin/EditablePlugin";
import CustomOnChangePlugin from "./plugins/CustomOnChangePlugin/CustomOnChangePlugin";
import CustomOnBlurPlugin from "./plugins/CustomOnBlurPlugin/CustomOnBlurPlugin";
import CustomOnTouchPlugin from "./plugins/CustomOnTouchPlugin/CustomOnTouchPlugin";
import { ReactNode, forwardRef, useCallback } from "react";
import classNames from "classnames";
import { Placeholder } from "./plugins/PlaceholderPlugin/PlaceholderPlugin";
import { editorConfig } from "./config/editorConfig";
import LoadInitialValuePlugin, {
  LoadInitialValuePluginRef,
} from "./plugins/LoadInitialValuePlugin/LoadInitialValuePlugin";

interface AscRichTextEditorProps {
  id?: string;
  debug?: boolean;
  className?: string;
  inputWrapperClassname?: string;
  header?: React.ReactNode;
  headerClassName?: string;
  bodyClassName?: string;
  toolbarBorder?: boolean;
  headerPosition?: "left" | "bottom" | "top" | "right";
  toolbarOptions?: Partial<typeof ToolBarOptions>;
  onTouch?: () => void;
  onBlur?: (value: Record<"json" | "html", string>) => void;
  onHtmlChange?: (value: string) => void;
  onJsonChange?: (value: string) => void;
  setFieldValue?: (name: string, value: string) => void;
  name?: string;
  initialInferValue?: string;
  initialHtmlValue?: string;
  initialJsonValue?: string;
  editable?: boolean;
  customContent?: ReactNode;
  placeholder?: string;
  disableAutoFocus?: boolean;
}

const AscRichTextEditor = forwardRef<
  LoadInitialValuePluginRef,
  AscRichTextEditorProps
>(
  (
    {
      debug = false,
      className,
      id,
      header,
      headerClassName,
      bodyClassName,
      inputWrapperClassname,
      toolbarOptions,
      toolbarBorder,
      headerPosition,
      onTouch,
      onBlur,
      onHtmlChange,
      onJsonChange,
      setFieldValue,
      name,
      initialHtmlValue,
      initialJsonValue,
      initialInferValue,
      editable = true,
      customContent,
      placeholder,
      disableAutoFocus = false,
    },
    ref
  ) => {
    const handleChange = useCallback(
      (value: Record<"json" | "html", string>) => {
        onJsonChange?.(value?.json);
        onHtmlChange?.(value?.html);
        if (name) {
          setFieldValue?.(name, value?.html);
        }
      },
      [onJsonChange, onHtmlChange, setFieldValue, name]
    );
    return (
      <LexicalComposer initialConfig={editorConfig} key={id ?? ""}>
        <div className={classNames(styles["editor-container"], className)}>
          <div className={styles.toolbarBox}>
            {editable && (
              <ToolbarPlugin
                toolbarOptions={toolbarOptions}
                header={header}
                headerClassName={headerClassName}
                toolbarBorder={toolbarBorder}
                headerPosition={headerPosition}
              />
            )}
          </div>
          <div className={classNames(styles["editor-inner"], bodyClassName)}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={classNames(
                    styles["editor-input"],
                    inputWrapperClassname
                  )}
                />
              }
              placeholder={<Placeholder text={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            {debug ? <TreeViewPlugin /> : null}
            {!disableAutoFocus && <AutoFocusPlugin />}
            <ListPlugin />
            <ClearEditorPlugin />
            <LinkPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <CustomOnChangePlugin onChange={handleChange} />
            {onBlur && <CustomOnBlurPlugin onBlur={onBlur} />}
            <CustomOnTouchPlugin
              initialValue={
                initialInferValue || initialHtmlValue || initialJsonValue
              }
              onTouch={onTouch}
            />
            <LoadInitialValuePlugin
              ref={ref}
              inferString={initialInferValue}
              htmlString={initialHtmlValue}
              jsonString={initialJsonValue}
            />
            <EditablePlugin editable={editable} />
          </div>
          {customContent && customContent}
        </div>
      </LexicalComposer>
    );
  }
);

export default AscRichTextEditor;
